// formula.js — THE formula evaluator, shared by server (product-db.js) and
// client (web/product-resolver.js). One implementation, zero divergence.
// Documented for users in FORMULAS.md (repo root) — keep the two in sync.
//
// Language (C-family syntax, same conventions as Onshape/Excel-style tools):
//   numbers        3, 2.5, .75
//   parameters     any configuration param name (word chars, case-sensitive)
//   arithmetic     + - * /        power: ^ (right-associative: 2^3^2 = 512)
//   grouping       ( … )          unary minus: -x
//   comparison     <  <=  >  >=  ==  !=      → 1 (true) / 0 (false)
//   logic          &&  ||                     (non-zero = true)
//   conditional    condition ? value_if_true : value_if_false
//   functions      min(a,b,…) max(a,b,…) round(x) floor(x) ceil(x) sqrt(x) abs(x)
//
// STRICT by design (2026-07-02, after "(Diameter^2)" silently evaluated as just
// Diameter): unknown characters, unknown names, unclosed parentheses, and ANY
// unconsumed trailing input are ERRORS — never silently ignored. Errors surface
// as ⚠ in the grids via the errRef out-param.
//
// evalFormula(expr, env, errRef?) → number | null
//   expr:   string (optional leading '=' is stripped)
//   env:    { paramName: value }
//   errRef: optional { } — on failure, errRef.msg is set to the reason

const FUNCS = {
  min: (...a) => Math.min(...a),
  max: (...a) => Math.max(...a),
  round: (x) => Math.round(x),
  floor: (x) => Math.floor(x),
  ceil: (x) => Math.ceil(x),
  sqrt: (x) => Math.sqrt(x),
  abs: (x) => Math.abs(x),
  // Trig, 2026-07-31. RADIANS, like Onshape FeatureScript, Excel and every other
  // C-family evaluator — write sin(deg*pi/180) for degrees. Added with `pi`
  // because a circumference or a bend allowance is unwritable without both.
  sin: (x) => Math.sin(x),
  cos: (x) => Math.cos(x),
  tan: (x) => Math.tan(x),
  atan2: (y, x) => Math.atan2(y, x),
};

// CONSTANTS (Alex, 2026-07-31: "is the variable pi standard?"). It was not — this
// evaluator had none, so `pi` failed as an unknown name, correctly but uselessly.
// Excel spells it PI(), Onshape FeatureScript spells it PI; here it is a bare name.
//
// A PARAMETER OF THE SAME NAME WINS. Constants are consulted only after `env`, so
// no existing formula can change meaning by our adding a name — the strictness that
// caught "(Diameter^2)" is worth more than owning the identifier. Zero stored
// formulas mention pi today (checked), so nothing is shadowed right now either.
const CONSTS = { pi: Math.PI, e: Math.E };

export function evalFormula(expr, env, errRef) {
  if (!expr || typeof expr !== 'string') return null;
  const src = expr.replace(/^\s*=\s*/, '').trim();
  if (!src) return null;
  let p = 0;

  const ws = () => { while (p < src.length && /\s/.test(src[p])) p++; };
  const peek2 = () => src.slice(p, p + 2);
  const fail = (msg) => { throw new Error(msg); };

  function atom() {
    ws();
    if (p >= src.length) fail('unexpected end of formula');
    if (src[p] === '(') {
      p++;
      const v = expr_();
      ws();
      if (src[p] !== ')') fail('missing closing ")"');
      p++;
      return v;
    }
    if (/[\d.]/.test(src[p])) {
      let s = '';
      while (p < src.length && /[\d.]/.test(src[p])) s += src[p++];
      const n = parseFloat(s);
      if (!Number.isFinite(n)) fail(`bad number: ${s}`);
      return n;
    }
    if (/[a-zA-Z_]/.test(src[p])) {
      let s = '';
      while (p < src.length && /\w/.test(src[p])) s += src[p++];
      ws();
      if (src[p] === '(') {
        // function call
        const fn = FUNCS[s.toLowerCase()];
        if (!fn) fail(`unknown function: ${s}`);
        p++;
        const args = [];
        ws();
        if (src[p] === ')') { p++; }
        else {
          for (;;) {
            args.push(expr_());
            ws();
            if (src[p] === ',') { p++; continue; }
            if (src[p] === ')') { p++; break; }
            fail('missing closing ")" in function call');
          }
        }
        if (args.length === 0) fail(`${s}() needs at least one argument`);
        return fn(...args);
      }
      // env FIRST, constants second — see the CONSTS note above: adding a name
      // must never change what an existing formula already means.
      if (s in env) return Number(env[s]);
      if (s.toLowerCase() in CONSTS) return CONSTS[s.toLowerCase()];
      fail(`unknown: ${s}`);
    }
    fail(`unexpected character: "${src[p]}"`);
  }

  // ^ — power, right-associative, binds tighter than * /.
  // Unary minus binds LOOSER than ^ (math convention: -x^2 = -(x^2), unlike
  // Excel's (-x)^2 gotcha); write (-x)^2 explicitly when that's the intent.
  function power() {
    ws();
    if (src[p] === '-') { p++; return -power(); }
    const base = atom();
    ws();
    if (src[p] === '^') { p++; return Math.pow(base, power()); }
    return base;
  }

  function term() {
    let v = power();
    ws();
    while (p < src.length && (src[p] === '*' || src[p] === '/')) {
      const op = src[p++];
      const r = power();
      ws();
      v = op === '*' ? v * r : v / r;
    }
    return v;
  }

  function additive() {
    let v = term();
    ws();
    // '-' here is a binary minus; unary minus is handled in atom().
    while (p < src.length && (src[p] === '+' || src[p] === '-')) {
      const op = src[p++];
      const r = term();
      ws();
      v = op === '+' ? v + r : v - r;
    }
    return v;
  }

  function comparison() {
    const v = additive();
    ws();
    const two = peek2();
    if (two === '<=' || two === '>=' || two === '==' || two === '!=') {
      p += 2;
      const r = additive();
      return (two === '<=' ? v <= r : two === '>=' ? v >= r : two === '==' ? v === r : v !== r) ? 1 : 0;
    }
    if (src[p] === '<' || src[p] === '>') {
      const op = src[p++];
      const r = additive();
      return (op === '<' ? v < r : v > r) ? 1 : 0;
    }
    return v;
  }

  function logical() {
    let v = comparison();
    ws();
    while (peek2() === '&&' || peek2() === '||') {
      const op = peek2();
      p += 2;
      const r = comparison();
      ws();
      v = op === '&&' ? ((v !== 0 && r !== 0) ? 1 : 0) : ((v !== 0 || r !== 0) ? 1 : 0);
    }
    return v;
  }

  // condition ? value_if_true : value_if_false (right-associative)
  function expr_() {
    const cond = logical();
    ws();
    if (src[p] === '?') {
      p++;
      const a = expr_();
      ws();
      if (src[p] !== ':') fail('missing ":" in condition ? a : b');
      p++;
      const b = expr_();
      return cond !== 0 ? a : b;
    }
    return cond;
  }

  try {
    const r = expr_();
    ws();
    if (p < src.length) fail(`unexpected trailing input: "${src.slice(p)}"`);
    if (!Number.isFinite(r)) fail('result is not a finite number');
    return r;
  } catch (e) {
    if (errRef) errRef.msg = String(e.message || e);
    return null;
  }
}
